// BOORO: tasks as single lines in the agent's conversation, each opening its own sub-chat.
import { taskPanelAction, taskWorkingRunStatus } from "@booro/tasks";
import { Trans, useLingui } from "@lingui/react/macro";
import { ChatMarkdown } from "@rakazo/chat-ui/web";
import type {
  Artifact,
  LoginFieldInput,
  TaskDetail,
  TaskDisplayStatus,
  TaskRun,
  TaskSubtask,
  TaskThreadEntry,
  TaskTimeline,
  ThreadMessage,
} from "@rakazo/contracts";
import { isSecretAskBlock } from "@rakazo/core";
import { BotAvatar, Button, Input } from "@rakazo/ui-web";
import type { LucideIcon } from "lucide-react";
import {
  Ban,
  ChevronRight,
  CircleAlert,
  CircleCheck,
  CircleDashed,
  CirclePause,
  LoaderCircle,
  X,
} from "lucide-react";
import type { ReactNode } from "react";
import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from "react";
import { useSearchParams } from "react-router-dom";
import type { GalleryItem } from "../../components/ArtifactGallery";
import { ArtifactGallery, artifactGalleryItem } from "../../components/ArtifactGallery";
import type { AskBlock } from "../../components/AskCard";
import { AskCard } from "../../components/AskCard";
import { Shimmer } from "../../components/ai/primitives";
import { TaskReviewActions } from "../../components/TaskReviewActions";
import {
  LIVE_TOOL_STEP_WINDOW,
  ToolActivityDisclosure,
  ToolSteps,
} from "../../components/ToolActivityDisclosure";
import { formatRelativeTime } from "../../lib/relative-time";
import type { RpcClient } from "../../lib/rpc";
import { rpc } from "../../lib/rpc";
import { useTaskDisplayStatusLabels } from "../../lib/task-display-status";
import { taskModelLabel } from "../../lib/task-model";
import { toolStepCount } from "../../lib/tool-activity-view";
import { errorText } from "../../lib/user-error";

const TASK_PARAM = "task";

type TaskThreadValue = {
  rows: readonly TaskThreadEntry[];
  botId: string;
  open: (ticketId: string) => void;
  reload: () => Promise<void>;
};

// The open conversation's tasks, shared with the transcript without threading new props
// through it. Only one shell is mounted at a time.
let currentTaskThread: TaskThreadValue | null = null;
const taskThreadListeners = new Set<() => void>();

function publishTaskThread(value: TaskThreadValue | null): void {
  currentTaskThread = value;
  for (const listener of taskThreadListeners) listener();
}

function subscribeTaskThread(listener: () => void): () => void {
  taskThreadListeners.add(listener);
  return () => taskThreadListeners.delete(listener);
}

export function useTaskThreadRows(): TaskThreadValue | null {
  return useSyncExternalStore(
    subscribeTaskThread,
    () => currentTaskThread,
    () => null,
  );
}

const STATUS_ICONS: Record<TaskDisplayStatus, LucideIcon> = {
  new: CircleDashed,
  working: LoaderCircle,
  waiting: CirclePause,
  blocked: CircleAlert,
  done: CircleCheck,
  dropped: Ban,
};

/**
 * Loads the agent's tasks for its conversation and keeps them current with the board.
 * Returns the provider for the transcript and the open sub-chat panel.
 */
export function useTaskThread(input: { botId: string | null; enabled: boolean }): {
  panel: ReactNode;
} {
  const [rows, setRows] = useState<TaskThreadEntry[]>([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const openTicketId = searchParams.get(TASK_PARAM);
  const botId = input.enabled ? input.botId : null;

  const reload = useCallback(async () => {
    if (!botId) return;
    const next = await rpc.booroTasks.forBot({ botId });
    setRows(next);
  }, [botId]);

  useEffect(() => {
    setRows([]);
    if (!botId) return;
    let active = true;
    const controller = new AbortController();
    void reload().catch(() => undefined);
    void (async () => {
      while (active) {
        try {
          const stream = await rpc.boards.subscribe(undefined, { signal: controller.signal });
          for await (const _event of stream) {
            if (!active) return;
            await reload().catch(() => undefined);
          }
        } catch {
          // The stream ends on navigation or a dropped connection; retry below.
        }
        if (!active) return;
        await new Promise((resolve) => setTimeout(resolve, 1_000));
      }
    })();
    return () => {
      active = false;
      controller.abort();
    };
  }, [botId, reload]);

  const open = useCallback(
    (ticketId: string) =>
      setSearchParams((previous) => {
        const next = new URLSearchParams(previous);
        next.set(TASK_PARAM, ticketId);
        return next;
      }),
    [setSearchParams],
  );
  const close = useCallback(
    () =>
      setSearchParams((previous) => {
        const next = new URLSearchParams(previous);
        next.delete(TASK_PARAM);
        return next;
      }),
    [setSearchParams],
  );

  const value = useMemo(
    () => (botId ? { rows, botId, open, reload } : null),
    [rows, botId, open, reload],
  );
  useEffect(() => {
    publishTaskThread(value);
    return () => publishTaskThread(null);
  }, [value]);
  const entry = openTicketId ? rows.find((row) => row.ticketId === openTicketId) : undefined;
  return {
    panel:
      botId && openTicketId ? (
        <TaskThreadPanel
          key={openTicketId}
          ticketId={openTicketId}
          botId={botId}
          entry={entry}
          onClose={close}
          onChanged={reload}
        />
      ) : null,
  };
}

/**
 * A task as one card in a conversation. What needs the person — its files, its open
 * question, its review — hangs below the line, so the sub-chat is only for details.
 */
export function TaskRow({
  task,
  botId,
  onOpen,
  onChanged,
  client = rpc,
  spaceId,
  onOpenSubtask,
}: {
  task: TaskThreadEntry;
  /** The conversation's agent; the task's assignee answers where the list has several. */
  botId: string;
  onOpen: () => void;
  /** Opens the sub-chat of work this task handed on; without it the lines only show it. */
  onOpenSubtask?: (subtask: TaskSubtask) => void;
  onChanged: () => Promise<void>;
  /** The office of the task; the selected one by default. */
  client?: RpcClient;
  spaceId?: string;
}) {
  const statuses = useTaskDisplayStatusLabels();
  const Icon = STATUS_ICONS[task.displayStatus];
  const asks = task.ask?.blocks.filter((block): block is AskBlock => block.kind === "ask") ?? [];
  const attached =
    task.subtasks.length > 0 ||
    task.deliverables.length > 0 ||
    asks.length > 0 ||
    task.waitsForReview;
  const subtasksEnded = task.subtasks.filter(
    (subtask) => subtask.displayStatus === "done" || subtask.displayStatus === "dropped",
  ).length;
  const answer = async (
    text: string,
    fields?: LoginFieldInput[],
    remember?: false,
    dismiss?: true,
  ) => {
    if (!task.ask) return;
    await client.threads.answer({
      botId: task.assignee?.botId ?? botId,
      runId: task.ask.runId ?? "",
      messageId: task.ask.id,
      answer: text,
      ...(fields ? { fields } : {}),
      ...(remember === false ? { remember } : {}),
      ...(dismiss ? { dismiss } : {}),
    });
    await onChanged();
  };
  return (
    <div className="flex justify-start py-0.5">
      <div
        data-testid={`task-thread-card-${task.ticketId}`}
        className={`max-w-full min-w-0 rounded-xl border border-border ${attached ? "w-full max-w-md" : ""}`}
      >
        <button
          type="button"
          data-testid={`task-thread-row-${task.ticketId}`}
          data-task-status={task.displayStatus}
          onClick={onOpen}
          className={`group/task flex w-full min-w-0 items-center gap-2 px-3 py-2 text-left text-[14px] hover:bg-muted ${
            attached ? "rounded-t-xl" : "rounded-xl"
          }`}
        >
          <Icon
            size={16}
            strokeWidth={1.8}
            aria-label={statuses[task.displayStatus]}
            className={`shrink-0 text-muted-foreground ${task.displayStatus === "working" ? "animate-spin" : ""}`}
          />
          {task.assignee ? (
            <span title={task.assignee.name} className="flex shrink-0">
              <BotAvatar color={task.assignee.color} identity={task.assignee.botId} size={18} />
            </span>
          ) : null}
          <span className="min-w-0 truncate font-medium text-foreground/90" dir="auto">
            {task.title}
          </span>
          {task.result ? (
            <span className="hidden min-w-0 truncate text-muted-foreground sm:inline" dir="auto">
              {task.result}
            </span>
          ) : task.subtasks.length > 0 ? (
            <span className="shrink-0 text-muted-foreground tabular-nums">
              {subtasksEnded}/{task.subtasks.length}
            </span>
          ) : (
            <span className="shrink-0 text-muted-foreground">{statuses[task.displayStatus]}</span>
          )}
          <ChevronRight
            size={14}
            aria-hidden="true"
            className="ms-auto shrink-0 text-muted-foreground/70 group-hover/task:text-foreground/70"
          />
        </button>
        {attached ? (
          <div
            data-testid={`task-thread-attached-${task.ticketId}`}
            className="space-y-2 border-t border-border px-3 py-2.5"
          >
            {task.subtasks.length > 0 ? (
              <ul className="-mx-1 space-y-0.5">
                {task.subtasks.map((subtask) => (
                  <li key={subtask.ticketId}>
                    <SubtaskLine
                      subtask={subtask}
                      statusLabel={statuses[subtask.displayStatus]}
                      onOpen={onOpenSubtask ? () => onOpenSubtask(subtask) : undefined}
                    />
                  </li>
                ))}
              </ul>
            ) : null}
            <ArtifactGallery
              target={{ ticketId: task.ticketId, ...(spaceId ? { spaceId } : {}) }}
              items={task.deliverables.map(artifactGalleryItem)}
              imageLimit={4}
            />
            {asks.map((block, index) => (
              <AskCard
                key={`${task.ask?.id}:${index}`}
                block={block}
                canAnswer
                canDismiss={isSecretAskBlock(block)}
                onAnswer={(text, fields, remember) => answer(text, fields, remember)}
                onDismiss={() => answer("", undefined, undefined, true)}
              />
            ))}
            {task.waitsForReview ? (
              <TaskReviewActions ticketId={task.ticketId} client={client} onChanged={onChanged} />
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
}

/** Work handed on, as one line: its state, who does it and what. */
function SubtaskLine({
  subtask,
  statusLabel,
  onOpen,
}: {
  subtask: TaskSubtask;
  statusLabel: string;
  onOpen?: () => void;
}) {
  const Icon = STATUS_ICONS[subtask.displayStatus];
  // Only what needs the person is spelled out; the icon says the rest.
  const needsPerson = subtask.displayStatus === "waiting" || subtask.displayStatus === "blocked";
  const content = (
    <>
      <Icon
        size={14}
        strokeWidth={1.8}
        aria-label={statusLabel}
        className={`shrink-0 text-muted-foreground ${subtask.displayStatus === "working" ? "animate-spin" : ""}`}
      />
      {subtask.assignee ? (
        <span title={subtask.assignee.name} className="flex shrink-0">
          <BotAvatar color={subtask.assignee.color} identity={subtask.assignee.botId} size={16} />
        </span>
      ) : null}
      <span
        className={`min-w-0 truncate ${subtask.displayStatus === "done" || subtask.displayStatus === "dropped" ? "text-muted-foreground" : "text-foreground/90"}`}
        dir="auto"
      >
        {subtask.title}
      </span>
      {needsPerson ? (
        <span className="ms-auto shrink-0 text-muted-foreground">{statusLabel}</span>
      ) : null}
    </>
  );
  const className = "flex w-full min-w-0 items-center gap-2 rounded-md px-1 py-1 text-[13px]";
  return onOpen ? (
    <button
      type="button"
      data-testid={`task-subtask-${subtask.ticketId}`}
      onClick={onOpen}
      className={`${className} text-left hover:bg-muted`}
    >
      {content}
    </button>
  ) : (
    <div data-testid={`task-subtask-${subtask.ticketId}`} className={className}>
      {content}
    </div>
  );
}

type ToolStep = Extract<ThreadMessage["blocks"][number], { kind: "steps" }>["steps"][number];

/** The step the agent is on right now, from the live row of its run. */
function currentStep(messages: readonly ThreadMessage[]): ToolStep | null {
  const live = messages.findLast((message) => message.id.startsWith("progress:"));
  const steps = live?.blocks.findLast((block) => block.kind === "steps");
  return steps?.kind === "steps" ? (steps.steps.at(-1) ?? null) : null;
}

/** Reloads the task on every change of its runs, reconnecting when the stream drops. */
function useTaskLive(ticketId: string, reload: () => Promise<void>) {
  useEffect(() => {
    let active = true;
    let running = false;
    let again = false;
    const controller = new AbortController();
    // Live steps arrive fast; one reload runs at a time and the last change is never lost.
    const schedule = async () => {
      if (running) {
        again = true;
        return;
      }
      running = true;
      try {
        do {
          again = false;
          await reload().catch(() => undefined);
        } while (again && active);
      } finally {
        running = false;
      }
    };
    void (async () => {
      while (active) {
        try {
          const stream = await rpc.booroTasks.subscribe(
            { ticketId },
            { signal: controller.signal },
          );
          for await (const _event of stream) {
            if (!active) return;
            void schedule();
          }
        } catch {
          // The stream ends on close or a dropped connection; retry below.
        }
        if (!active) return;
        await new Promise((resolve) => setTimeout(resolve, 1_000));
      }
    })();
    return () => {
      active = false;
      controller.abort();
    };
  }, [ticketId, reload]);
}

function TaskThreadPanel({
  ticketId,
  botId,
  entry,
  onClose,
  onChanged,
}: {
  ticketId: string;
  botId: string;
  entry: TaskThreadEntry | undefined;
  onClose: () => void;
  onChanged: () => Promise<void>;
}) {
  const { t } = useLingui();
  const statuses = useTaskDisplayStatusLabels();
  const [detail, setDetail] = useState<TaskDetail | null>(null);
  const [timeline, setTimeline] = useState<TaskTimeline | null>(null);
  const [deliverables, setDeliverables] = useState<readonly Artifact[]>([]);
  const [error, setError] = useState("");
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const displayStatus = detail?.displayStatus ?? entry?.displayStatus;
  const latestRun = detail?.runs.at(-1);
  const action = detail ? taskPanelAction(detail.ticket.status, latestRun?.status) : null;
  const live = Boolean(taskWorkingRunStatus(latestRun?.status));
  const title = detail?.ticket.title ?? entry?.title;
  const refText = detail?.ticket.ref ?? entry?.ref;
  const result = detail?.meta.result ?? entry?.result;
  const lastModel = detail?.runs.findLast((run) => run.modelId);
  const model = lastModel ? taskModelLabel(lastModel.modelProvider, lastModel.modelId) : null;
  const step = live && timeline ? currentStep(timeline.messages) : null;
  const StatusIcon = displayStatus ? STATUS_ICONS[displayStatus] : null;

  const load = useCallback(async () => {
    const [nextDetail, nextTimeline, nextDeliverables] = await Promise.all([
      rpc.booroTasks.get({ ticketId }),
      rpc.booroTasks.timeline({ ticketId }),
      rpc.booroTasks.deliverables({ ticketId }),
    ]);
    setDetail(nextDetail);
    setTimeline(nextTimeline);
    setDeliverables(nextDeliverables);
  }, [ticketId]);

  const refresh = useCallback(async () => {
    try {
      await load();
      setError("");
      await onChanged();
    } catch (cause) {
      setError(errorText(cause));
    }
  }, [load, onChanged]);

  // The board moved the task (another tab, the agent itself): read it again.
  useEffect(() => {
    let active = true;
    void load().catch((cause) => {
      if (active) setError(errorText(cause));
    });
    return () => {
      active = false;
    };
  }, [load, entry?.displayStatus, entry?.result]);
  useTaskLive(ticketId, load);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      // A viewer open above the panel closes first.
      if (event.key === "Escape" && !document.querySelector('[role="dialog"]')) onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  // The newest entry stays in view, like in the chat.
  const itemCount = (timeline?.messages.length ?? 0) + (timeline?.comments.length ?? 0);
  const stepLabel = step?.label;
  useEffect(() => {
    const node = scrollRef.current;
    if (node) node.scrollTop = node.scrollHeight;
  }, [itemCount, stepLabel, action]);

  async function act(run: () => Promise<unknown>, fallback?: string) {
    setBusy(true);
    setError("");
    try {
      await run();
      await refresh();
    } catch (cause) {
      setError(errorText(cause, fallback));
    } finally {
      setBusy(false);
    }
  }

  async function send() {
    const text = draft.trim();
    if (!text) return;
    await act(async () => {
      await rpc.booroTasks.send({ ticketId, text });
      setDraft("");
    });
  }

  const answer = useCallback(
    async (
      message: ThreadMessage,
      text: string,
      fields?: LoginFieldInput[],
      remember?: false,
      dismiss?: true,
    ) => {
      await rpc.threads.answer({
        botId: detail?.ticket.assigneeBotId ?? botId,
        runId: message.runId ?? "",
        messageId: message.id,
        answer: text,
        ...(fields ? { fields } : {}),
        ...(remember === false ? { remember } : {}),
        ...(dismiss ? { dismiss } : {}),
      });
      await refresh();
    },
    [botId, detail?.ticket.assigneeBotId, refresh],
  );

  return (
    <aside
      data-testid="task-thread-panel"
      aria-label={title ?? t`Task`}
      className="fixed inset-y-0 end-0 z-50 flex w-full max-w-md flex-col border-s border-border bg-background shadow-xl"
    >
      <div className="flex items-start gap-2 border-b border-border px-4 py-3">
        <div className="min-w-0 flex-1">
          <p
            data-testid="task-thread-status"
            className="flex min-w-0 items-center gap-1.5 text-xs text-muted-foreground"
          >
            {StatusIcon && displayStatus ? (
              <>
                <StatusIcon
                  size={13}
                  strokeWidth={1.8}
                  aria-hidden="true"
                  className={`shrink-0 ${displayStatus === "working" ? "animate-spin" : ""}`}
                />
                <span className="shrink-0">{statuses[displayStatus]}</span>
              </>
            ) : null}
            {step ? (
              <span className="min-w-0 truncate" title={step.label}>
                · <Shimmer>{step.label}</Shimmer>
              </span>
            ) : null}
          </p>
          <h2 className="truncate text-[15px] font-medium text-foreground" dir="auto">
            {title}
          </h2>
          {refText || model ? (
            <button
              type="button"
              aria-expanded={showDetails}
              onClick={() => setShowDetails((open) => !open)}
              className="mt-0.5 flex items-center gap-0.5 text-xs text-muted-foreground hover:text-foreground/75"
            >
              <ChevronRight
                size={12}
                aria-hidden="true"
                className={`transition-transform motion-reduce:transition-none ${
                  showDetails ? "rotate-90" : ""
                }`}
              />
              <Trans>Details</Trans>
            </button>
          ) : null}
          {showDetails ? (
            <div className="mt-1 space-y-0.5 text-xs text-muted-foreground">
              {refText ? <p>{refText}</p> : null}
              {model ? (
                <p className="truncate" dir="auto" title={model}>
                  <Trans>Model</Trans>: {model}
                </p>
              ) : null}
            </div>
          ) : null}
        </div>
        {action === "stop" ? (
          <Button
            size="sm"
            variant="outline"
            disabled={busy}
            onClick={() =>
              void act(() => rpc.booroTasks.stop({ ticketId }), t`Could not stop the agent`)
            }
          >
            <Trans>Stop</Trans>
          </Button>
        ) : action === "resume" ? (
          <Button
            size="sm"
            disabled={busy}
            onClick={() => void act(() => rpc.tickets.update({ id: ticketId, status: "todo" }))}
          >
            <Trans>Resume</Trans>
          </Button>
        ) : null}
        <Button variant="ghost" size="icon-sm" aria-label={t`Close`} onClick={onClose}>
          <X />
        </Button>
      </div>
      <div ref={scrollRef} className="rk-scroll flex-1 space-y-3 overflow-y-auto px-4 py-4">
        {title ? (
          <div
            data-testid="task-thread-brief"
            className="rounded-xl border border-border px-3.5 py-2.5"
          >
            <p className="mb-1 text-xs text-muted-foreground">
              <Trans>Task brief</Trans>
            </p>
            <div className="text-sm" dir="auto">
              <ChatMarkdown>{detail?.ticket.description?.trim() || title}</ChatMarkdown>
            </div>
          </div>
        ) : null}
        {timeline ? (
          <TaskTimelineView
            ticketId={ticketId}
            timeline={timeline}
            runs={detail?.runs ?? null}
            onAnswer={answer}
          />
        ) : null}
        {live && !step ? (
          <p className="text-sm">
            <Shimmer>
              <Trans>Working…</Trans>
            </Shimmer>
          </p>
        ) : null}
        {action === "review" ||
        (displayStatus === "done" && (result || deliverables.length > 0)) ? (
          <div
            data-testid="task-thread-result"
            className="space-y-2 rounded-xl border border-border bg-card px-3.5 py-2.5"
          >
            <p className="text-xs text-muted-foreground">
              <Trans>Result</Trans>
            </p>
            {result ? (
              <p className="text-sm text-foreground" dir="auto">
                {result}
              </p>
            ) : null}
            <ArtifactGallery target={{ ticketId }} items={deliverables.map(artifactGalleryItem)} />
            {action === "review" ? (
              <TaskReviewActions ticketId={ticketId} onChanged={refresh} requestChanges={false} />
            ) : null}
          </div>
        ) : null}
        {error ? (
          <p role="alert" className="text-sm text-destructive">
            {error}
          </p>
        ) : null}
      </div>
      <form
        className="flex items-center gap-2 border-t border-border px-3 py-2.5"
        onSubmit={(event) => {
          event.preventDefault();
          void send();
        }}
      >
        <Input
          aria-label={t`Write to the agent`}
          placeholder={t`Write to the agent`}
          value={draft}
          disabled={!detail?.ticket.assigneeBotId}
          onChange={(event) => setDraft(event.target.value)}
        />
        <Button type="submit" size="sm" disabled={busy || !draft.trim()}>
          <Trans>Send</Trans>
        </Button>
      </form>
    </aside>
  );
}

type AnswerTask = (
  message: ThreadMessage,
  text: string,
  fields?: LoginFieldInput[],
  remember?: false,
  dismiss?: true,
) => Promise<void>;

type TimelineItem =
  | { kind: "message"; at: string; message: ThreadMessage }
  | { kind: "comment"; at: string; comment: TaskTimeline["comments"][number] };

function TaskTimelineView({
  ticketId,
  timeline,
  runs,
  onAnswer,
}: {
  ticketId: string;
  timeline: TaskTimeline;
  runs: readonly TaskRun[] | null;
  onAnswer: AnswerTask;
}) {
  const items: TimelineItem[] = [
    ...timeline.messages.map((message) => ({
      kind: "message" as const,
      at: message.createdAt,
      message,
    })),
    ...timeline.comments.map((comment) => ({
      kind: "comment" as const,
      at: comment.createdAt,
      comment,
    })),
  ].sort((a, b) => (a.at < b.at ? -1 : a.at > b.at ? 1 : 0));
  return (
    <>
      {items.map((item) =>
        item.kind === "comment" ? (
          <TimelineComment key={item.comment.id} comment={item.comment} />
        ) : (
          <TimelineMessage
            key={item.message.id}
            ticketId={ticketId}
            message={item.message}
            live={item.message.id.startsWith("progress:")}
            runs={runs}
            onAnswer={onAnswer}
          />
        ),
      )}
    </>
  );
}

/**
 * Whether a comment is the person's. A comment an agent writes with its ticket tool
 * carries the run's user too, so the bot author decides.
 */
export function commentIsFromPerson(comment: TaskTimeline["comments"][number]): boolean {
  return Boolean(comment.authorUserId) && !comment.authorBotId;
}

/** The person's comments read like their messages; the agent's get the bot bubble. */
function TimelineComment({ comment }: { comment: TaskTimeline["comments"][number] }) {
  const at = formatRelativeTime(comment.createdAt);
  if (commentIsFromPerson(comment)) {
    return (
      <div
        className="rk-user-message ms-8 rounded-2xl bg-chat-user px-3.5 py-2.5 text-sm text-chat-user-foreground"
        dir="auto"
        title={at}
      >
        <p className="whitespace-pre-wrap break-words">{comment.body}</p>
      </div>
    );
  }
  return (
    <div
      className="space-y-1 rounded-2xl bg-muted px-3.5 py-2.5 text-sm text-foreground/90"
      dir="auto"
      title={at}
    >
      {comment.authorName ? (
        <p className="text-xs text-muted-foreground">{comment.authorName}</p>
      ) : null}
      <ChatMarkdown>{comment.body}</ChatMarkdown>
    </div>
  );
}

function TimelineMessage({
  ticketId,
  message,
  live,
  runs,
  onAnswer,
}: {
  ticketId: string;
  message: ThreadMessage;
  live: boolean;
  runs: readonly TaskRun[] | null;
  onAnswer: AnswerTask;
}) {
  const run = runs?.find((candidate) => candidate.runId === message.runId);
  const waiting = run?.status === "waiting_input" || run?.status === "waiting_takeover";
  // BOORO: a helper's approval card is answered while the run keeps working.
  const working = run?.status === "leased" || run?.status === "running";
  const files = message.blocks.flatMap((block): GalleryItem[] =>
    block.kind === "file" || block.kind === "image"
      ? [
          {
            artifactId: block.artifactId,
            name: block.name,
            mimeType: block.mimeType,
            size: block.kind === "file" ? block.size : 0,
          },
        ]
      : [],
  );
  const firstFileIndex = message.blocks.findIndex(
    (block) => block.kind === "file" || block.kind === "image",
  );
  const blocks = message.blocks.flatMap((block, index) => {
    if (block.kind === "steps") {
      return [
        <ToolActivityDisclosure
          key={index}
          live={live}
          stepCount={toolStepCount(block.steps)}
          durationMs={block.durationMs}
        >
          <ToolSteps
            steps={block.steps}
            currentIndex={live ? block.steps.length - 1 : undefined}
            limit={live ? LIVE_TOOL_STEP_WINDOW : undefined}
          />
        </ToolActivityDisclosure>,
      ];
    }
    if (block.kind === "ask") {
      return runs ? (
        <AskCard
          key={index}
          block={block}
          canAnswer={
            waiting || (working && Boolean(block.approvalEffectId) && !block.instructionUpdate)
          }
          canDismiss={waiting && isSecretAskBlock(block)}
          onAnswer={(text, fields, remember) => onAnswer(message, text, fields, remember)}
          onDismiss={() => onAnswer(message, "", undefined, undefined, true)}
        />
      ) : block.text.trim() ? (
        [<ChatMarkdown key={index}>{block.text}</ChatMarkdown>]
      ) : (
        []
      );
    }
    if (block.kind === "text" || block.kind === "progress") {
      return block.text.trim() ? [<ChatMarkdown key={index}>{block.text}</ChatMarkdown>] : [];
    }
    // The message's files show together, at the place of the first one.
    if (block.kind === "file" || block.kind === "image") {
      if (index !== firstFileIndex) return [];
      return [<ArtifactGallery key={index} target={{ ticketId }} items={files} />];
    }
    return [];
  });
  if (blocks.length === 0) return null;
  const mine = message.role === "user";
  // Steps alone are a slim line between messages, not a bubble.
  const toolsOnly = !mine && message.blocks.every((block) => block.kind === "steps");
  return (
    <div
      className={
        toolsOnly
          ? "text-sm"
          : `space-y-2 rounded-2xl px-3.5 py-2.5 text-sm ${
              mine ? "rk-user-message ms-8 bg-chat-user text-chat-user-foreground" : "bg-muted"
            }`
      }
      dir="auto"
      title={formatRelativeTime(message.createdAt)}
    >
      {blocks}
    </div>
  );
}
